import React, { useEffect, useState } from "react";
import PageToolbar from "../../../components/RestaurantAdminComponents/PageToolbar";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import { IconAvatar } from "../../../components/Common/PageWidgets";
import ActivityDetailModal from "../../../components/Common/ActivityDetailModal";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";

import eyeIcon from "../../../assets/images/user-dashboard/eye.svg";
import coinIcon from "../../../assets/images/user-dashboard/voucher.svg";
import starIcon from "../../../assets/images/user-dashboard/star.svg";

/* -------------------------------------------------------------------------
 * AdminActivityLogsPage — full activity logs table view.
 * Similar pattern to CustomersPage, showing all admin activity logs.
 * ---------------------------------------------------------------------- */

const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const cleanActivityText = (value, fallback = "") => {
  const text = stripHtmlTags(value).trim();
  if (!text || text.toLowerCase() === "default" || text.includes("App\\Models")) return fallback;
  return text;
};

const ACTIVITY_ICON = {
  api_login: { icon: starIcon, tone: "teal" },
  default: { icon: coinIcon, tone: "blue" },
};

const getActivityIcon = (activity) => ACTIVITY_ICON[activity?.event] || ACTIVITY_ICON.default;

const columns = (onView) => [
  {
    key: "description",
    label: "Activity",
    width: "3fr",
    render: (row) => {
      const { icon, tone } = getActivityIcon(row);
      return (
        <span className="data-table__row-label" style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>
          <IconAvatar icon={icon} tone={tone} size={32} />
          <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            {cleanActivityText(row.description, "Activity recorded")}
          </span>
        </span>
      );
    },
  },
  {
    key: "type",
    label: "Type",
    width: "1fr",
    render: (row) => cleanActivityText(row.type, "Activity"),
  },
  {
    key: "relative_date",
    label: "Date",
    width: "1fr",
    render: (row) => row.date || "Unknown",
  },
  {
    key: "view",
    label: "",
    width: "64px",
    align: "center",
    render: (row) => (
      <img
        src={eyeIcon}
        alt="View"
        onClick={() => onView && onView(row.id)}
        style={{ cursor: 'pointer', width: '20px', height: '20px' }}
      />
    ),
  },
];

const AdminActivityLogsPage = () => {
  document.title = "Activity Logs | Omaha Food Lovers";
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);

    return params.toString();
  };

  const fetchActivities = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminActivityLogs}?${buildQueryParams(pageNum)}`);

      if (response.data && response.data.data) {
        const activitiesData = response.data.data;
        const newActivities = Array.isArray(activitiesData) ? activitiesData : (activitiesData.data || []);
        const currentPage = activitiesData.current_page || response.data.current_page || pageNum;
        const lastPage = activitiesData.last_page || response.data.last_page || pageNum;

        if (isLoadMore) {
          setActivities((prev) => [...prev, ...newActivities]);
        } else {
          setActivities(newActivities);
        }

        setHasMore(currentPage < lastPage);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchActivities(page + 1, true);
    }
  };

  useEffect(() => {
    fetchActivities(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewActivity = async (activityId) => {
    const act = activities.find(a => a.id === activityId);
    if (act) {
      const { icon } = getActivityIcon(act);
      setSelectedActivity({ ...act, icon });
      setIsModalOpen(true);
      try {
        const response = await get(endpoints.adminActivityLog.replace('{{activity_id}}', activityId));
        const detail = response.data?.data || response.data;
        if (detail) setSelectedActivity({ ...detail, icon: getActivityIcon(detail).icon });
      } catch (error) {
        console.error("Failed to fetch activity detail:", error);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
  };

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Activity Logs</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of admin activity and system events.</p>
          </div>
        <PageToolbar
          title=""
          showFilter={false}
          showSearch={false}
          showExport={false}
        />

        <DataTable
          columns={columns(handleViewActivity)}
          rows={activities}
          getRowKey={(row) => row.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
        </div>
      </Container>

      <ActivityDetailModal
        activity={selectedActivity}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

AdminActivityLogsPage.propTypes = {};

AdminActivityLogsPage.defaultProps = {};

export default AdminActivityLogsPage;
